import React, { useEffect, lazy, Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { UIProvider, useUI } from './context/UIContext';
import Navigation from './components/Navigation';
import UserSwitcher from './components/UserSwitcher';
import TaskStatusRecovery from './components/TaskStatusRecovery';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import './App.css';

// Seiten werden einzeln nachgeladen. Vorher lag alles in einem Chunk:
// 2,9 MB (830 KB gzip). Durch das Splitten kommt der Start nur noch mit dem
// wirklich benoetigten Code zurecht - pdf.js, Tesseract (OCR), jsPDF und
// ECharts werden erst geladen, wenn die jeweilige Seite geoeffnet wird.
const Dashboard            = lazy(() => import('./pages/Dashboard'));
const Expenses             = lazy(() => import('./pages/Expenses'));
const AddExpense           = lazy(() => import('./pages/AddExpense'));
const CustomerCredits      = lazy(() => import('./pages/CustomerCredits'));
const CustomerSegmentation = lazy(() => import('./pages/CustomerSegmentation'));
const Tasks                = lazy(() => import('./pages/Tasks'));
const TaskCalendar         = lazy(() => import('./pages/TaskCalendar'));
const Revenues             = lazy(() => import('./pages/Revenues'));
const TaxReport            = lazy(() => import('./pages/TaxReport'));
const ReceiptArchive       = lazy(() => import('./pages/ReceiptArchive'));
const ReceiptOcrReview     = lazy(() => import('./pages/ReceiptOcrReview'));
const ExpenseAnalysis      = lazy(() => import('./pages/ExpenseAnalysis'));
const ActivityLog          = lazy(() => import('./pages/ActivityLog'));
const Settings             = lazy(() => import('./pages/Settings'));
const CommissionStatements = lazy(() => import('./pages/CommissionStatements'));
const PayoutReconciliation  = lazy(() => import('./pages/PayoutReconciliation'));

// WORKAROUND: Dieser Kommentar dient nur dazu, die GitHub-Erkennung in der Questera-Plattform auszulösen.

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useApp();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 font-medium">Laden...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const AppLayout = ({ children }) => {
  const { sidebarCollapsed } = useUI();
  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 font-sans text-gray-900 dark:text-gray-100">
      <Navigation />
      <div className={`flex-1 flex flex-col ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} relative min-w-0 transition-all duration-300`}>
        <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex justify-end items-center">
          <UserSwitcher />
        </div>
        
        <main className="flex-1 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};

const AppRoutes = () => {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-3"></div>
          <p className="text-gray-500 text-sm">Seite wird geladen…</p>
        </div>
      </div>
    }>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute><AppLayout><Expenses /></AppLayout></ProtectedRoute>} />
        <Route path="/add-expense" element={<ProtectedRoute><AppLayout><AddExpense /></AppLayout></ProtectedRoute>} />
        <Route path="/customers" element={<ProtectedRoute><AppLayout><CustomerCredits /></AppLayout></ProtectedRoute>} />
        <Route path="/commission-statements" element={<ProtectedRoute><AppLayout><CommissionStatements /></AppLayout></ProtectedRoute>} />
        <Route path="/payout-reconciliation" element={<ProtectedRoute><AppLayout><PayoutReconciliation /></AppLayout></ProtectedRoute>} />
        <Route path="/segmentation" element={<ProtectedRoute><AppLayout><CustomerSegmentation /></AppLayout></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><AppLayout><Tasks /></AppLayout></ProtectedRoute>} />
        <Route path="/task-calendar" element={<ProtectedRoute><AppLayout><TaskCalendar /></AppLayout></ProtectedRoute>} />
        <Route path="/revenues" element={<ProtectedRoute><AppLayout><Revenues /></AppLayout></ProtectedRoute>} />
        <Route path="/tax-report" element={<ProtectedRoute><AppLayout><TaxReport /></AppLayout></ProtectedRoute>} />
        <Route path="/receipts" element={<ProtectedRoute><AppLayout><ReceiptArchive /></AppLayout></ProtectedRoute>} />
        <Route path="/receipt-ocr" element={<ProtectedRoute><AppLayout><ReceiptOcrReview /></AppLayout></ProtectedRoute>} />
        <Route path="/analysis" element={<ProtectedRoute><AppLayout><ExpenseAnalysis /></AppLayout></ProtectedRoute>} />
        <Route path="/activity" element={<ProtectedRoute><AppLayout><ActivityLog /></AppLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><AppLayout><Settings /></AppLayout></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

const ThemeWrapper = ({ children }) => {
  const { settings } = useApp();

  useEffect(() => {
    const root = document.documentElement;
    
    if (settings.theme === 'dark') {
      root.classList.add('dark');
    } else if (settings.theme === 'light') {
      root.classList.remove('dark');
    } else if (settings.theme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        if (e.matches) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [settings.theme]);

  return children;
};

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error('React Error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Etwas ist schiefgelaufen</h1>
            <p className="text-gray-700 mb-4">Die App hat einen Fehler. Versuche einen Browser-Refresh (Strg+Shift+R).</p>
            <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-64">
              {String(this.state.error?.message || this.state.error || 'Unbekannter Fehler')}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
            >
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <TaskStatusRecovery />
        <UIProvider>
          <ThemeWrapper>
            <Router>
              <AppRoutes />
            </Router>
          </ThemeWrapper>
        </UIProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;